import React, { useState, useEffect, useCallback, useRef } from "react";

import { makeStyles } from "@mui/styles";
import {
  List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Button, Tooltip, Typography, CircularProgress,
  Breadcrumbs, Link, TextField,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(() => ({
  root: { display: "flex", flexDirection: "column", gap: 4 },
  toolbar: { display: "flex", alignItems: "center", gap: 4 },
  crumbs: { flex: 1, minWidth: 0, "& ol": { flexWrap: "nowrap", overflow: "hidden" } },
  list: { maxHeight: 260, overflowY: "auto", padding: 0 },
  empty: { padding: 12, textAlign: "center", color: "#8A9096", fontSize: 13 },
  fileSize: { color: "#8A9096", fontSize: 11, marginLeft: 8, flex: "none" },
}));

const fmtSize = bytes => {
  const n = Number(bytes);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const iconFor = file => {
  if (file.isFolder) return <FolderIcon style={{ color: "#F37021" }} />;
  const m = file.mimeType || "";
  if (m.startsWith("image/")) return <ImageIcon color="primary" />;
  if (m === "application/pdf") return <PictureAsPdfIcon color="error" />;
  if (m.startsWith("audio/")) return <AudiotrackIcon color="action" />;
  if (m.startsWith("video/")) return <MovieIcon color="action" />;
  return <InsertDriveFileIcon color="action" />;
};

/**
 * Explorer da pasta do cliente no Google Drive (fase 2 / E1).
 * Tudo passa pelo backend: listagem, download e upload — o arquivo sobe
 * como a conta da empresa, o atendente não precisa de acesso ao Drive.
 */
const DriveExplorer = ({ contact }) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState([]); // [{id, name}] — raiz implícita
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const inputRef = useRef();

  const currentFolderId = stack.length ? stack[stack.length - 1].id : null;

  const load = useCallback(async () => {
    if (!contact?.id || !contact?.driveFolderId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/drive/files`, {
        params: currentFolderId ? { folderId: currentFolderId } : {},
      });
      setFiles(data.files || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [contact?.id, contact?.driveFolderId, currentFolderId]);

  useEffect(() => { load(); }, [load]);
  // Contato mudou → volta pra raiz
  useEffect(() => { setStack([]); }, [contact?.id]);

  const handleOpen = file => {
    if (file.isFolder) setStack(prev => [...prev, { id: file.id, name: file.name }]);
    else handleDownload(file);
  };

  const handleDownload = async file => {
    try {
      const { data } = await api.get(
        `/contacts/${contact.id}/drive/files/${file.id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpload = async e => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    e.target.value = "";
    const form = new FormData();
    selected.forEach(f => form.append("medias", f));
    if (currentFolderId) form.append("folderId", currentFolderId);
    setLoading(true);
    try {
      await api.post(`/contacts/${contact.id}/drive/files`, form);
      toast.success(selected.length > 1 ? `${selected.length} arquivos enviados` : "Arquivo enviado");
      await load();
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return setNewFolderMode(false);
    try {
      await api.post(`/contacts/${contact.id}/drive/folders`, {
        name, parentId: currentFolderId || undefined,
      });
      setNewFolderMode(false);
      setNewFolderName("");
      await load();
    } catch (err) {
      toastError(err);
    }
  };

  if (!contact?.driveFolderId) {
    return (
      <div className={classes.empty}>
        Este cliente ainda não tem pasta no Drive.
        <br />Ela é criada ao aceitar um atendimento dele.
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <Breadcrumbs maxItems={3} itemsAfterCollapse={2} className={classes.crumbs} separator="›">
          <Link component="button" underline="hover" variant="body2" onClick={() => setStack([])}>
            📁 Pasta
          </Link>
          {stack.map((f, i) => (
            <Link
              key={f.id}
              component="button"
              underline="hover"
              variant="body2"
              onClick={() => setStack(stack.slice(0, i + 1))}
            >
              {f.name}
            </Link>
          ))}
        </Breadcrumbs>
        <Tooltip title="Enviar arquivos pra esta pasta (sobem na conta da empresa)">
          <IconButton size="small" onClick={() => inputRef.current?.click()}>
            <UploadFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Nova subpasta">
          <IconButton size="small" onClick={() => setNewFolderMode(v => !v)}>
            <CreateNewFolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Atualizar">
          <IconButton size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Abrir no Google Drive">
          <IconButton size="small" onClick={() => window.open(contact.driveFolderUrl, "_blank", "noopener")}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input ref={inputRef} type="file" multiple hidden onChange={handleUpload} />
      </div>

      {newFolderMode && (
        <div style={{ display: "flex", gap: 4 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Nome da subpasta"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCreateFolder()}
          />
          <Button size="small" variant="contained" onClick={handleCreateFolder}>OK</Button>
        </div>
      )}

      {loading ? (
        <div className={classes.empty}><CircularProgress size={20} /></div>
      ) : files.length === 0 ? (
        <div className={classes.empty}>
          Pasta vazia — use o botão de upload ⬆️ pra guardar arquivos deste cliente aqui.
        </div>
      ) : (
        <List dense className={classes.list}>
          {files.map(f => (
            <ListItemButton key={f.id} onClick={() => handleOpen(f)} style={{ paddingTop: 2, paddingBottom: 2 }}>
              <ListItemIcon style={{ minWidth: 34 }}>{iconFor(f)}</ListItemIcon>
              <ListItemText
                primary={f.name}
                primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
              />
              {!f.isFolder && (
                <Typography component="span" className={classes.fileSize}>
                  {fmtSize(f.size)}
                </Typography>
              )}
            </ListItemButton>
          ))}
        </List>
      )}
    </div>
  );
};

export default DriveExplorer;
